import { contentFound, contentNotFound, viewContent } from '../utils/eightlytics/build-payload'
import { cloneEvent } from '../utils/event'
import getUrlParameter from '../utils/get-url-parameter'
import injectIframe from '../utils/inject-iframe'
import isDefined from '../utils/is-defined'
import isWidgetHalfVisible from '../utils/is-widget-half-visible'
import logger from '../utils/logger'
import { pub, sub, unsub } from '../utils/pub-sub'
import { clean } from '../utils/url'
import widgetPrototypes from './prototypes'

function Widget({ id, node, container, window, config }) {
  // never change the eternalId!
  // it is used to identify the widget in tracking events - no matter of internal technical changes inside the SDK
  this.eternalId = config.eternalId || 'widget'
  this.type = config.type || 'widget'
  this.id = id
  this.node = node
  this.container = container
  this.window = window
  this.config = config

  const normalizedTouchpoint = (node.getAttribute('data-touchpoint') || '').toLowerCase()
  const widgetConfigKey = config.type
  const widgetConfigKeyWithTouchpoint = `${widgetConfigKey}_${normalizedTouchpoint}`

  const widgetConfig =
    window._8select.config.widgets[widgetConfigKeyWithTouchpoint] ||
    window._8select.config.widgets[widgetConfigKey] ||
    {}

  this.widgetConfiguration = {
    apiUrl: config.apiUrl,
    apiUrlIncludesApiId: false,
    widgetUrl: 'https://wgt.8select.io/8.widget-app/index.html',
    customWidgetStyles: '',
    customizationOptions: config.customizationOptions || {},
    ...widgetConfig,
  }
  this.isActive = false
  this.isDestroyed = false

  const dynamicSetId = getUrlParameter(this.widgetConfiguration['dynamicSetIdParameter'])
  if (dynamicSetId !== undefined) {
    this.node.setAttribute('data-set-id', dynamicSetId)
  }

  if (
    !this.widgetConfiguration.subscriptionOptout ||
    !this.widgetConfiguration.subscriptionOptout.includes('app:content:found')
  ) {
    sub('app:content:found', this.id, context => {
      if (!context || context.widgetId !== id) {
        return
      }
      this.window.eightlytics('loadSet', contentFound(context, this.iframe.contentWindow.window), undefined, id)
      this.activate(context)
    })
  }
  if (
    !this.widgetConfiguration.subscriptionOptout ||
    !this.widgetConfiguration.subscriptionOptout.includes('app:content:notFound')
  ) {
    sub('app:content:notFound', this.id, context => {
      if (!context || context.widgetId !== id) {
        return
      }

      this.window.eightlytics('loadSet', contentNotFound(context, this.iframe.contentWindow.window), undefined, id)
      this.destroy()
    })
  }
  if (
    !this.widgetConfiguration.subscriptionOptout ||
    !this.widgetConfiguration.subscriptionOptout.includes('app:error')
  ) {
    sub('app:error', this.id, context => {
      if (!context || context.widgetId !== id) {
        return
      }

      this.window.eightlytics('loadSet', contentNotFound(context, this.iframe.contentWindow.window), undefined, id)
      this.destroy()
    })
  }
  if (
    !this.widgetConfiguration.subscriptionOptout ||
    !this.widgetConfiguration.subscriptionOptout.includes('widget:removedFromDom')
  ) {
    sub('widget:removedFromDom', this.id, context => {
      if (!context || context.widgetId !== id) {
        return
      }

      this.destroy()
    })
  }

  for (const subscription of this.widgetConfiguration.subscriptions || []) {
    sub(subscription.event, this.id, subscription.callback.bind(this))
  }

  if (typeof this.window._8select.config.hooks.createWidget === 'function') {
    this.window._8select.config.hooks.createWidget(this)
  }
}

Object.assign(Widget.prototype, widgetPrototypes)

Widget.prototype.load = function(eltxLoadCallback) {
  if (!isDefined(this.widgetConfiguration.apiUrl)) {
    console.error(
      `8SELECT: API URL not set - can not fetch your recommendation content.
      Please contact your 8SELECT account manager and include a screenshot of this message.`,
      this.config,
      this.widgetConfiguration
    )
    return
  }

  if (!isDefined(this.widgetConfiguration.widgetUrl)) {
    console.error(
      `8SELECT: Widget app URL not set - can not fetch your recommendation content.
      Please contact your 8SELECT account manager and include a screenshot of this message.`,
      this.config,
      this.widgetConfiguration
    )
    return
  }

  this.node.setAttribute('data-8select-widget-state', 'loading')
  // get identifier like sku or set-id
  const identifier = this.getIdentifier()
  const setId = this.getSetId()
  if ((!isDefined(identifier) || identifier === '') && (!isDefined(setId) || setId === '')) {
    console.error(
      `8SELECT: Widget has neither a SKU nor a setId parameter - can not fetch your recommendation content.
      Please contact your 8SELECT account manager and include a screenshot of this message.`,
      this.config,
      this.widgetConfiguration,
      this.node
    )

    return this.destroy()
  }
  const widgetUuid = this.getWidgetUuid()

  eltxLoadCallback()

  // @todo allow to add info about breakpoint from the parent page - so the widget can react to it
  const config = {
    'widget-type': this.type,
    'widget-uuid': widgetUuid,
    sku: identifier,
    'set-id': setId,
    loadDistanceFactor: this.node.getAttribute('data-load-distance-factor'),
    url: this.widgetConfiguration.widgetUrl,
    display: 'inline-block',
  }

  let api = clean(
    `${this.widgetConfiguration.apiUrl}/${this.getApiId()}/${identifier}`,
    this.widgetConfiguration.apiQuery
  )

  // only do this for 8.SET-Custom - the other content types do not have any "/sets" API endpoint
  if (setId && (this.config.type === 'SetCustom' || this.config.type === 'SetPhoto')) {
    api = clean(
      `${this.widgetConfiguration.apiUrl}/sets/${this.getApiId()}/${setId}`,
      this.widgetConfiguration.apiQuery
    )
  }

  const widgetConfig = {
    api,
    sku: identifier,
    setId,
    uuid: widgetUuid,
    customizationOptions: this.widgetConfiguration.customizationOptions,
    pub: (eventName, appContext) => {
      pub(`app:${eventName}`, { widgetId: this.id, widgetType: this.type, api, appContext })
    },
  }

  const callback = function(error) {
    if (error) {
      this.destroy()
      return
    }

    this.addListeners()
    this.node.setAttribute('data-8select-widget-state', 'loaded')
    this.applyCustomStyles()
  }.bind(this)

  this.iframe = injectIframe(this.window, config, this.node, callback, widgetConfig)
}

Widget.prototype.addListeners = function() {
  const body = this.iframe.contentWindow.document.body
  let originalHeight = body.offsetHeight || body.scrollHeight

  this.resizeInterval = setInterval(
    function() {
      try {
        var body = this.iframe.contentWindow.document.body
        var newHeight = body.offsetHeight || body.scrollHeight
        if (newHeight === originalHeight) {
          return
        }
        originalHeight = newHeight
        this.iframe.style.height = newHeight + 'px'
      } catch (error) {
        logger('Widget was removed from DOM. Cleaning up event listeners.', { error })
        this.destroy()
      }
    }.bind(this),
    300
  )

  this.injectEvent = function(event) {
    try {
      var newEvent = cloneEvent(event)
      this.iframe.contentWindow.dispatchEvent(newEvent)
    } catch (error) {
      logger('Widget was removed from DOM. Cleaning up event listeners.', { error })
    }
  }.bind(this)

  this.window.addEventListener('scroll', this.injectEvent)
  this.iframe.contentWindow.addEventListener('wheel', this.window._8select.utils.cloneAndDispatchEvent)
  this.iframe.contentWindow.addEventListener('touchmove', this.window._8select.utils.cloneAndDispatchEvent)
}

Widget.prototype.applyCustomStyles = function() {
  const body = this.iframe.contentWindow.document.body
  const customWidgetStyles = this.widgetConfiguration.customWidgetStyles

  if (!body || !customWidgetStyles) {
    return
  }

  const styleSheet = document.createElement('style')
  styleSheet.type = 'text/css'
  styleSheet.setAttribute('name', 'cs-8s-stylesheet')
  styleSheet.innerHTML = customWidgetStyles

  body.insertAdjacentElement('beforebegin', styleSheet)
}

Widget.prototype.activate = function(context) {
  if (this.isActive === true) {
    return
  }
  this.isActive = true

  this.show()

  const viewSetTimeout = () =>
    setTimeout(() => {
      const widgetHasBeenSeen = isWidgetHalfVisible(this)
      if (widgetHasBeenSeen) {
        pub('widget:view', context)

        return this.window.eightlytics('viewSet', viewContent(context), undefined, this.id)
      }
      viewSetTimeout()
    }, 300)
  viewSetTimeout()

  pub('widget:activate', context)
}

Widget.prototype.destroy = function() {
  if (this.isDestroyed === true) {
    return
  }
  this.isDestroyed = true

  try {
    unsub(this.id)

    this.node.setAttribute('data-8select-widget-state', 'destroyed')

    if (this.resizeInterval) {
      clearInterval(this.resizeInterval)
    }
    this.window.removeEventListener('scroll', this.injectEvent)

    if (this.iframe.contentWindow) {
      this.iframe.contentWindow.removeEventListener('wheel', this.window._8select.utils.cloneAndDispatchEvent)
      this.iframe.contentWindow.removeEventListener('touchmove', this.window._8select.utils.cloneAndDispatchEvent)
    }

    if (this.node.hasChildNodes()) {
      this.node.removeChild(this.iframe)
    }
    this.window.removeEventListener('scroll', this.scrollCallback)

    this.hide()
    pub('widget:destroy', { widgetId: this.id, widgetType: this.type })
  } catch (error) {
    logger('Widget was removed from DOM. Error during clean up of event listeners.', {
      widgetConfiguration: this.widgetConfiguration,
      error,
    })
  }
}

export default Widget
